import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme, CircularProgress } from "@mui/material";
import { FormPropertyProps } from "../../FormProperty";
import CalendarIcon from "src/assets/icons/calendar.svg?react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { formatDateTime } from "src/utils/formatting";
import locale from "dayjs/locale/*";
import { formatUnitAbbreviation } from "src/utils/formatting";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { manageTooltipText } from "src/utils/tooltips";
import { sanitizeRichTextHtml } from "src/components/HTMLBox/sanitizeRichTextHtml";
import { getDisplayValueForScore } from "src/utils/scores";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { usePropertyContext } from "../../context/usePropertyContext";
import { FieldValueLayout } from "../FieldValueLayout";

export const Date = (props: FormPropertyProps) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const { selectedScorePrecision } = useEntityScores();
    const { property, position, showSubscore, dimensionValue } = props;
    const { post, onUpdate, scoreUnit, canEdit, getElement } = usePropertyContext();
    const element = getElement(property.id, position);

    const defaultValue = property.defaultValue ?? "";
    const initialValue = element?.value ?? "";

    const [locale, setLocale] = useState<locale.Locale | null>(null);
    const [value, setValue] = useState(dayjs(initialValue).format("DD-MM-YYYY"));
    const [genericValueSelected, setGenericValueSelected] = useState<boolean>(element?.isGenericValue ?? false);

    const genericValue = property.genericValue;
    const genericValueLabel = genericValue ?? "";

    const propertyScoreValue = OlapScoresService.normalizeNumericalScore(dimensionValue?.score.value);
    const shouldDisplayScore = propertyScoreValue !== null && showSubscore === true;

    const scoreProperty =
        propertyScoreValue !== null ?
            {
                score:
                    shouldDisplayScore !== false ?
                        getDisplayValueForScore(propertyScoreValue, selectedScorePrecision)
                    :   null,
                unit: scoreUnit,
            }
        :   undefined;

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    if (!post) return <CircularProgress />;

    const unit = formatUnitAbbreviation(property.unit);

    // [ds] to localize for other langs, doing dynamic import based on i18n.resolvedLanguage can be done.
    // For now dynamic imports break Vite which has to be properly configured. This may do the trick:
    // https://github.com/vite-plugin/vite-plugin-dynamic-import#readme
    if (locale === null) {
        import("dayjs/locale/fr").then((locale: locale.Locale) => {
            setLocale(locale);
        });
    }

    const updatePropertyValue = (value?: string, isGenericValue?: boolean) => {
        onUpdate({
            postId: post.id,
            propertyId: Number(property.id),
            value: value ?? "",
            position: Number(props.position ?? 0),
            isGenericValue: isGenericValue,
        });
    };

    const tooltipText = manageTooltipText((coalescedText) => {
        return <div dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(coalescedText) }} />;
    }, property);

    return (
        <FieldValueLayout
            property={property}
            element={element}
            layoutOptions={{
                defaultValue: defaultValue ?? null,
                genericValue: genericValue ?? null,
                isGenericValueSelected: genericValueSelected,
                dimensionValue,
                forceHideScore: showSubscore === false,
                setDefaultValue: () => {
                    if (value === dayjs(defaultValue).format("DD/MM/YYYY")) {
                        return;
                    }

                    setValue(dayjs(defaultValue).format(locale?.formats.L));
                    updatePropertyValue(dayjs(defaultValue).format("YYYY-MM-DD"));
                },
                setGenericValue: () => {
                    setGenericValueSelected((prevState) => !prevState);
                    setValue(!genericValueSelected ? dayjs(genericValueLabel).format("DD/MM/YYYY") : "");
                    updatePropertyValue(
                        !genericValueSelected ? dayjs(genericValueLabel).format("YYYY-MM-DD") : "",
                        !genericValueSelected,
                    );
                },
                hideUnit: true,
            }}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.resolvedLanguage}>
                <DatePicker
                    disabled={!canEdit || property.readonly || genericValueSelected}
                    value={dayjs(value, locale?.formats.L)}
                    onChange={(date: Dayjs | null) => {
                        if (!date) {
                            return;
                        }

                        const value = date.format(locale?.formats.L);

                        setValue(value);
                        updatePropertyValue(dayjs(date).format("YYYY-MM-DD"));
                    }}
                    format={locale?.formats.L}
                    dayOfWeekFormatter={(date: Dayjs) => {
                        const weekday = formatDateTime(date.valueOf(), i18n.resolvedLanguage, { weekday: "short" });

                        return weekday.charAt(0).toUpperCase() + weekday.substring(1, 3);
                    }}
                    slots={{
                        openPickerIcon: CalendarIcon,
                    }}
                    slotProps={{
                        popper: {
                            sx: {
                                ".MuiPaper-root": {
                                    // width: "100%",
                                    boxShadow: "0 4px 20px 0 rgba(81, 78, 78, 0.12)",
                                    border: `1px solid ${theme.custom.grey[500]}`,
                                    borderRadius: "12px",
                                },
                            },
                        },
                        layout: {
                            sx: {
                                ".MuiPickersCalendarHeader-root": {
                                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                                    display: "flex",
                                    gap: "12px",
                                    padding: "12px",
                                    margin: 0,
                                    ".MuiPickersCalendarHeader-labelContainer": {
                                        ...theme.typography.body,
                                    },
                                },
                                ".MuiPickersYear-yearButton": {
                                    ...theme.typography.body,
                                    "&.Mui-selected": {
                                        background: `${theme.palette.primary[600]} !important`,
                                        color: "#fff",
                                    },
                                },
                                ".MuiDayCalendar-weekDayLabel": {
                                    color: theme.custom.grey[1400],
                                },
                                ".MuiPickersDay-today": {
                                    background: "#fff",
                                    borderColor: `${theme.palette.primary[600]} !important`,
                                },
                                ".MuiButtonBase-root": {
                                    ":hover": {
                                        background: theme.custom.grey[400],
                                    },
                                    "&.Mui-selected": {
                                        background: `${theme.palette.primary[600]} !important`,
                                        color: "#fff",
                                    },
                                },
                            },
                        },
                        textField: {
                            sx: {
                                width: "100%",
                                ".MuiInputBase-root": {
                                    height: "40px",
                                    borderRadius: "8px",
                                    border: `1px solid ${theme.custom.grey[500]}`,
                                    ":hover": {
                                        backgroundColor: theme.custom.grey[300],
                                    },
                                    "&.Mui-disabled": {
                                        background: theme.custom.grey[500],
                                    },
                                    "&.Mui-focused": {
                                        outline: `2px solid ${theme.custom.grey[2800]}`,
                                    },
                                    ".MuiInputBase-input": {
                                        ...theme.typography.body,
                                        paddingInline: "20px",
                                    },
                                    ".MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                    },
                                },
                                ".MuiButtonBase-root": {
                                    background: "transparent",
                                    color: theme.custom.grey[2800],
                                    padding: "12px",
                                    ":hover": {
                                        background: "transparent",
                                    },
                                },
                            },
                        },
                    }}
                />
            </LocalizationProvider>
        </FieldValueLayout>
    );
};
